<!-- src/views/sources/users/index.vue -->
<script setup>
definePage({
  meta: { action: 'read', subject: 'source-user', navActiveLink: 'sources-users' },
})

import { getProjectId } from '@/utils/cdms'
import { useSnackBar } from '@/utils/stores/snackbar'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { VDataTable } from 'vuetify/labs/VDataTable'

/* ───────── 기본 ───────── */
const router = useRouter()
const snackbar = useSnackBar()
const projectLocked = ref(await isLockedProject())
const projectId = getProjectId()
const persistKey = computed(() => `assignSitesUsers:${projectId}`)
const getToday = () => new Date().toISOString().slice(0, 10)

/* ID 비교 유틸 */
const toKey  = v => String(v ?? '')
const sameId = (a, b) => toKey(a) === toKey(b)

/* ───────── 메인 사용자 표 ───────── */
const page = ref(1)
const itemsPerPage = ref(10)
const selected = ref([])
const search = ref('')
const users = ref([])

const headers = ref([
  { title: '사용자명', key: 'name',       width: '16%' },
  { title: '기관명',     key: 'site_name',  width: '16%' },
  { title: '역할',     key: 'role',       width: '10%' },
  { title: '이메일',   key: 'email',      width: '22%' },
  { title: '시작일',   key: 'start_date', width: '12%' },
  { title: '종료일',   key: 'end_date',   width: '12%' },
  { title: '상태',     key: 'status',     width: '8%'  },
  { title: '편집',     key: 'actions',    width: '90px', sortable: false },
])

async function loadUserTable () {
  const { data } = await useApi(createUrl('/sources/users', { query: { projectId } }))

  users.value = Array.isArray(data.value) ? data.value : []
}
onMounted(loadUserTable)

const filterItems = [
  { value: 'name',  title: '사용자명' },
  { value: 'email', title: '이메일'   },
]

const filterItem = ref('name')

const filterText = (value, query, item) =>
  value != null && query != null &&
  typeof value === 'string' &&
  String(item.columns[filterItem.value] || '').toLowerCase()
    .includes(String(query).toLowerCase())

/* ───────── 모달 상태 ───────── */
const isDialog = ref(false)
const saving = ref(false)
const q = ref('') // 모달 공통 검색

// 좌: 기관(전체행) + 선택ID
const siteRows = ref([])          // [{id,name,phone,investigator,startDate,endDate,registered}]
const selectedSiteIds = ref([])

// 우: 사용자(기관 선택에 따라 로드) + 선택ID
const rawUsersForDialog = ref([]) // 병합된 서버 원본
const selectedUserIds = ref([])

/* 기존 연결: siteKey -> userKey[] */
const existingBySite = ref({})

/* ───────── 데이터 로딩 (기관) ───────── */
function mapSiteRow(s, registered=false) {
  const id   = s.id ?? s.site_id ?? s.siteId
  
  return {
    id,
    name: s.name ?? s.site_name ?? '',
    phone: s.phone ?? s.tel ?? '',
    investigator: s.investigator ?? s.pi ?? '',
    startDate: s.startDate ?? s.start_date ?? '',
    endDate: s.endDate   ?? s.end_date   ?? '',
    registered,
  }
}
function dedupeByIdPreferExisting(list) {
  const map = new Map()
  for (const r of list) {
    const k = toKey(r.id)
    if (!k) continue
    const prev = map.get(k)
    if (!prev) map.set(k, r)
    else if (r.registered && !prev.registered) map.set(k, r)
  }
  
  return Array.from(map.values())
}
function remainingOrder(remainRows, existingRows) {
  const ex = new Set(existingRows.map(r => toKey(r.id)))
  
  return remainRows.filter(r => !ex.has(toKey(r.id)))
}

async function loadExistingAttachments () {
  const { data } = await useApi(createUrl('/sources/users', { query: { projectId } }))
  const arr = Array.isArray(data.value) ? data.value : []
  const tmp = {}
  for (const it of arr) {
    const sid = toKey(it.site_id ?? it.siteId ?? it.site?.id ?? it.site?.site_id)
    const uid = toKey(it.user_id ?? it.userId ?? it.user?.id ?? it.id)
    if (!sid || !uid) continue
    if (!tmp[sid]) tmp[sid] = []
    tmp[sid].push(uid)
  }
  for (const k in tmp) tmp[k] = Array.from(new Set(tmp[k]))
  existingBySite.value = tmp
}

async function loadSitesForDialog () {
  const { data: remainData }   = await useApi(createUrl('/sources/remain-sites', { query: { projectId } }))
  const { data: existingData } = await useApi(createUrl('/sources/sites',       { query: { projectId } }))

  const remain   = Array.isArray(remainData.value)   ? remainData.value   : []
  const existing = Array.isArray(existingData.value) ? existingData.value : []

  const remainRows   = remain.map(s => mapSiteRow(s, false))
  const existingRows = existing.map(s => mapSiteRow(s, true))

  let merged = dedupeByIdPreferExisting([...remainingOrder(remainRows, existingRows), ...existingRows])

  // ✅ 정렬: 등록됨(true) 먼저, 그다음 이름
  merged.sort((a, b) => Number(b.registered) - Number(a.registered) || String(a.name).localeCompare(String(b.name), 'ko'))

  // 로컬 복원(선택/날짜)
  const persisted = loadPersist()
  const wantedIds = (persisted.sites || []).map(s => toKey(s.id))
  const dateMap   = Object.fromEntries((persisted.sites || []).map(s => [toKey(s.id), s]))

  merged.forEach(r => {
    const d = dateMap[toKey(r.id)]
    if (d) {
      r.startDate = d.startDate || r.startDate || ''
      r.endDate   = d.endDate   || r.endDate   || ''
    }
  })

  siteRows.value = merged
  selectedSiteIds.value = [...wantedIds]
}

/* ───────── 데이터 로딩 (사용자) ───────── */
const isSelectableByStatus = status =>
  !/(expired|inactive|disabled|만료|비활성|탈퇴)/i.test(String(status ?? ''))

const userSiteKeys = u => {
  const ids = []
  if (u.site_id != null) ids.push(u.site_id)
  if (u.siteId  != null) ids.push(u.siteId)
  if (Array.isArray(u.siteIds)) ids.push(...u.siteIds)
  if (Array.isArray(u.sites))   ids.push(...u.sites.map(s => s.id))
  
  return [...new Set(ids.map(toKey))]
}

/** 선택된 기관에 "이미 연결된" 사용자(테이블의 users.value 기반) 추출 */
function existingUsersForSites (siteIds) {
  const chosen = new Set(siteIds.map(toKey))
  const acc = new Map() // uid -> obj
  for (const r of users.value) {
    const sid = toKey(r.site_id ?? r.siteId ?? r.site?.id)
    if (!chosen.has(sid)) continue
    const uid = toKey(r.user_id ?? r.userId ?? r.user?.id ?? r.id)
    if (!uid) continue
    const name  = r.name  ?? r.user?.name  ?? ''
    const email = r.email ?? r.user?.email ?? ''
    const role  = r.role  ?? 'Member'
    const status = r.status ?? '비활성'
    const cur = acc.get(uid) || { id: uid, name, email, role, status, siteIds: new Set() }

    cur.siteIds.add(sid)
    acc.set(uid, cur)
  }
  
  return Array.from(acc.values()).map(u => ({ ...u, siteIds: Array.from(u.siteIds) }))
}

/** 남은 유저(remain) + 기존 연결(existing)을 userId 기준으로 병합(기존연결 우선) */
function mergeUsersForDialog (remainList, existingList) {
  const map = new Map()
  for (const u of existingList) {
    const key = toKey(u.id)

    map.set(key, { ...u, _mergeExisting: true })
  }
  for (const u of remainList) {
    const key = toKey(u.id)
    if (!map.has(key)) map.set(key, { ...u })
    else {
      // siteIds 합치기
      const base = map.get(key)
      const union = new Set([...(base.siteIds || userSiteKeys(base)), ...userSiteKeys(u)])

      map.set(key, { ...base, ...u, siteIds: Array.from(union) })
    }
  }

  // 기존연결 우선 정렬 → 이름순
  return Array.from(map.values()).sort(
    (a, b) => Number(b._mergeExisting ? 1:0) - Number(a._mergeExisting ? 1:0)
          || String(a.name||'').localeCompare(String(b.name||''), 'ko'),
  )
}

/* 선택된 기관 기반으로 사용자 병합 로드 */
async function loadUsersForDialogBySites (siteIds) {
  if (!siteIds.length) {
    rawUsersForDialog.value = []
    selectedUserIds.value = []
    
    return
  }

  // 1) 남은 유저
  const { data } = await useApi(createUrl('/sources/remain-users', {
    query: { projectId, siteIds: siteIds.join(',') },
  }))

  console.log(data)

  const remain = Array.isArray(data.value) ? data.value : []
  const chosen = new Set(siteIds.map(toKey))

  const remainFiltered = remain.filter(u => {
    const uSites = userSiteKeys(u)
    if (!uSites.length) return true
    
    return uSites.some(id => chosen.has(id))
  })

  // 2) 기존 연결 유저 (테이블 데이터 기반)
  const exist = existingUsersForSites(siteIds)

  // 3) 병합
  rawUsersForDialog.value = mergeUsersForDialog(remainFiltered, exist)

  // 4) 선택 복원: (로컬 저장 ∪ 기존연결[선택기관]) ∩ 현재 후보
  const wantLocal = new Set((loadPersist().users || []).map(toKey))
  const wantExisting = new Set()
  for (const sid of siteIds.map(toKey)) {
    const arr = existingBySite.value[sid] || []

    arr.forEach(uid => wantExisting.add(toKey(uid)))
  }
  const unionWant = new Set([...wantLocal, ...wantExisting])

  selectedUserIds.value = rawUsersForDialog.value
    .filter(u => unionWant.has(toKey(u.id)))
    .map(u => u.id)
}

/* ───────── 모달 열기/닫기 ───────── */
async function openDialog () {
  if (projectLocked.value) return
  await loadUserTable()            // ← 기존 연결 최신화 보장
  await loadSitesForDialog()
  await loadExistingAttachments()
  await loadUsersForDialogBySites(selectedSiteIds.value)
  isDialog.value = true
}
const closeDialog = () => { isDialog.value = false }

/* ───────── 파생/유틸 ───────── */
const filteredSiteRows = computed(() => {
  const k = q.value.trim().toLowerCase()
  if (!k) return siteRows.value
  
  return siteRows.value.filter(s =>
    String(s.name||'').toLowerCase().includes(k) ||
    String(s.phone||'').toLowerCase().includes(k) ||
    String(s.investigator||'').toLowerCase().includes(k),
  )
})

/* 사용자 파생 */
const usersForDialog = computed(() => {
  const chosen = new Set(selectedSiteIds.value.map(toKey))
  
  return rawUsersForDialog.value.map(u => {
    const byStatus = isSelectableByStatus(u.status)
    const uSites   = userSiteKeys(u)
    const hasSite  = uSites.length > 0
    const inChosen = hasSite && uSites.some(id => chosen.has(id))
    const bySite   = hasSite && inChosen

    // 이미 연결?
    let already = false
    if (hasSite) {
      for (const sid of uSites.map(toKey)) {
        const arr = existingBySite.value[toKey(sid)] || []
        if (arr.includes(toKey(u.id)) && chosen.has(toKey(sid))) { already = true; break }
      }
    }
    
    return { ...u, selectable: byStatus && bySite, _hasSite: hasSite, _bySite: bySite, _already: already }
  })
})

const filteredUsersForDialog = computed(() => {
  const k = q.value.trim().toLowerCase()
  
  const base = (usersForDialog.value)
  if (!k) return base
  
  return base.filter(u =>
    String(u.name||'').toLowerCase().includes(k) ||
    String(u.email||'').toLowerCase().includes(k) ||
    String(u.role||'').toLowerCase().includes(k),
  )
})

const selectedSites = computed(() =>
  siteRows.value.filter(r => selectedSiteIds.value.some(x => sameId(x, r.id))),
)

const canSave = computed(() => selectedSites.value.length > 0)

const summaryText = computed(() => {
  const sCnt = selectedSites.value.length
  const uCnt = selectedUserIds.value.length
  if (!sCnt && !uCnt) return '선택된 내용이 없습니다.'
  
  return `선택된 기관 ${sCnt}개 · 선택된 사용자 ${uCnt}명`
})

/* ───────── 선택 토글 ───────── */
const isSiteSelected = id => selectedSiteIds.value.some(x => sameId(x, id))
function toggleSite(id) {
  const idx = selectedSiteIds.value.findIndex(x => sameId(x, id))
  if (idx >= 0) selectedSiteIds.value.splice(idx, 1)
  else selectedSiteIds.value.push(id)

  nextTick(async () => {
    await loadUsersForDialogBySites(selectedSiteIds.value)
    pruneInvalidUserSelections()
    savePersist()
  })
}

const isUserSelected = id => selectedUserIds.value.some(x => sameId(x, id))
function toggleUser(id, disabled=false) {
  if (disabled) return
  const idx = selectedUserIds.value.findIndex(x => sameId(x, id))
  if (idx >= 0) selectedUserIds.value.splice(idx, 1)
  else selectedUserIds.value.push(id)
  savePersist()
}

/* 선택 불가 자동 해제 */
function pruneInvalidUserSelections () {
  const allow = new Set(filteredUsersForDialog.value.filter(u => u.selectable).map(u => toKey(u.id)))

  selectedUserIds.value = selectedUserIds.value.filter(id => allow.has(toKey(id)))
}
watch([selectedSiteIds, rawUsersForDialog], pruneInvalidUserSelections)

/* ───────── 저장(추가/삭제 diff) ───────── */
// async function submitAll () {
//   if (saving.value || !canSave.value) return
//   saving.value = true
//   try {
//     // 1) 신규 기관만 등록
//     const newSites = selectedSites.value.filter(s => !s.registered)
//     if (newSites.length) {
//       await Promise.all(
//         newSites.map(s =>
//           $api('/sources/sites', {
//             method: 'POST',
//             body: {
//               site: {
//                 projectId,
//                 siteId: s.id,
//                 contractDate: s.startDate || null,
//                 endDate: s.endDate   || null,
//               },
//             },
//           }),
//         ),
//       )
//     }

//     // 2) 사용자 연결/해제 Diff
//     const picked = usersForDialog.value.filter(u =>
//       selectedUserIds.value.some(x => sameId(x, u.id)),
//     )

//     const addRequests = []
//     const delRequests = []

//     for (const s of selectedSites.value) {
//       const sidKey = toKey(s.id)

//       const desired = new Set(
//         picked
//           .filter(u => userSiteKeys(u).some(id => sameId(id, s.id)))
//           .map(u => toKey(u.id)),
//       )

//       const existing = new Set((existingBySite.value[sidKey] || []).map(toKey))

//       // Add
//       for (const uidKey of desired) {
//         if (!existing.has(uidKey)) {
//           addRequests.push(
//             $api('/sources/users', {
//               method: 'POST',
//               body: {
//                 projectId,
//                 data: {
//                   userId: uidKey,
//                   siteId: s.id,
//                   role: (picked.find(u => toKey(u.id) === uidKey)?.role) ?? 'Member',
//                   startDate: s.startDate || null,
//                   endDate: s.endDate   || null,
//                 },
//               },
//             }),
//           )
//         }
//       }

//       // Delete
//       for (const uidKey of existing) {
//         if (!desired.has(uidKey)) {
//           delRequests.push(
//             $api('/sources/usersDestroy', {
//               method: 'DELETE',
//               body: { projectId, data: { userId: uidKey, siteId: s.id } },
//             }),
//           )
//         }
//       }
//     }

//     if (delRequests.length > 1) {
//       const ok = window.confirm(`선택에서 제외된 사용자 ${delRequests.length}명을 연결 해제합니다. 진행하시겠습니까?`)
//       if (!ok) {
//         await Promise.all(addRequests)
//         snackbar.showSnackBar('추가 등록만 처리되었습니다.')
//         await loadUserTable()
//         await loadExistingAttachments()
//         savePersist()
//         closeDialog()
//         saving.value = false
        
//         return
//       }
//     }

//     await Promise.all([...delRequests, ...addRequests])

//     await loadUserTable()
//     await loadExistingAttachments()
//     savePersist()
//     snackbar.showSnackBar('등록되었습니다.')
//     closeDialog()
//   } finally {
//     saving.value = false
//   }
// }

// ───────── 저장(신규 추가 + 연결 해제 + 사이트 삭제) ─────────
async function submitAll () {
  if (saving.value || !canSave.value) return
  saving.value = true
  try {
    /* 0) '이번 저장' 기준 상태 계산 */
    const selectedKeys = new Set(selectedSiteIds.value.map(toKey))
    const initiallyRegisteredSites = siteRows.value.filter(s => s.registered)

    // 이번 저장에서 '제외'된(=등록되어 있었는데 선택 해제) 사이트
    const removedSites = initiallyRegisteredSites.filter(s => !selectedKeys.has(toKey(s.id)))

    // 삭제되는 사이트에 묶인 사용자 수 합산(알림용)
    const removedSitesUserCount = removedSites.reduce(
      (sum, s) => sum + ((existingBySite.value[toKey(s.id)] || []).length),
      0,
    )

    /* 1) 신규 기관만 등록 */
    const newSites = selectedSites.value.filter(s => !s.registered)
    if (newSites.length) {
      await Promise.all(
        newSites.map(s =>
          $api('/sources/sites', {
            method: 'POST',
            body: {
              site: {
                projectId,
                siteId: s.id,
                contractDate: s.startDate || null,
                endDate: s.endDate   || null,
              },
            },
          }),
        ),
      )
    }

    /* 2) 사용자 연결/해제 Diff (선택된 사이트만 대상) */
    const picked = usersForDialog.value.filter(u =>
      selectedUserIds.value.some(x => sameId(x, u.id)),
    )

    const addRequests = []
    const delRequests = []

    for (const s of selectedSites.value) {
      const sidKey = toKey(s.id)

      // 이 사이트에 최종적으로 연결되길 원하는 사용자
      const desired = new Set(
        picked
          .filter(u => userSiteKeys(u).some(id => sameId(id, s.id)))
          .map(u => toKey(u.id)),
      )

      // 현재 서버 기준으로 이 사이트에 연결된 사용자
      const existing = new Set((existingBySite.value[sidKey] || []).map(toKey))

      // Add
      for (const uidKey of desired) {
        if (!existing.has(uidKey)) {
          addRequests.push(
            $api('/sources/users', {
              method: 'POST',
              body: {
                projectId,
                data: {
                  userId: uidKey,
                  siteId: s.id,
                  role: (picked.find(u => toKey(u.id) === uidKey)?.role) ?? 'Member',
                  startDate: s.startDate || null,
                  endDate: s.endDate   || null,
                },
              },
            }),
          )
        }
      }

      // Delete(연결 해제) — 선택에서 빠진 사용자만
      for (const uidKey of existing) {
        if (!desired.has(uidKey)) {
          delRequests.push(
            $api('/sources/usersDestroy', {
              method: 'DELETE',
              body: { projectId, data: { userId: uidKey, siteId: s.id } },
            }),
          )
        }
      }
    }

   


    /* 3) 사이트 삭제 요청 준비 (/sites/{id}) */
    const siteDeleteRequests = removedSites.map(s =>{
      console.log({ projectId, siteId: s.id  })
      $api('/sources/sites', {
        method: 'DELETE',
        body: { projectId, siteId: s.id  }, // 선택한 사이트의 실제 siteId
      })},
    )

    /* 4) 사용자/사이트 삭제 확인 안내 */
    const totalDetach = delRequests.length
    const totalSiteDelete = removedSites.length
    const needsConfirm = (totalDetach > 0) || (totalSiteDelete > 0)

    if (needsConfirm) {
      // 안내 메시지 구성
      const parts = []
      if (totalSiteDelete) {
        parts.push(`제외된 기존 기관 ${totalSiteDelete}개 삭제`)
        if (removedSitesUserCount) {
          parts.push(`(해당 기관에 묶인 사용자 ${removedSitesUserCount}명도 함께 삭제)`)
        }
      }
      if (totalDetach) parts.push(`선택 해제된 사용자 연결 ${totalDetach}건 삭제`)
      const msg = `${parts.join(' · ')}\n진행하시겠습니까?`

      const ok = window.confirm(msg)
      if (!ok) {
        // 사용자/사이트 삭제는 스킵하고 추가만 진행
        if (addRequests.length) await Promise.all(addRequests)
        snackbar.showSnackBar('추가 등록만 처리되었습니다.')
        await loadUserTable()
        await loadExistingAttachments()
        savePersist()
        closeDialog()
        saving.value = false
        
        return
      }
    }

    /* 5) 실행 순서
       - 사이트 삭제 먼저(해당 사이트 매핑 사용자도 백엔드에서 함께 정리)
       - 사용자 연결 해제
       - 사용자 추가
    */
    if (siteDeleteRequests.length) await Promise.all(siteDeleteRequests)
    if (delRequests.length)       await Promise.all(delRequests)
    if (addRequests.length)       await Promise.all(addRequests)

    /* 6) 마무리 */
    await loadUserTable()
    await loadExistingAttachments()
    savePersist()
    snackbar.showSnackBar('처리가 완료되었습니다.')
    closeDialog()
  } finally {
    saving.value = false
  }
}


/* ───────── 보존/복원 ───────── */
function savePersist () {
  const data = {
    sites: selectedSites.value.map(s => ({
      id: s.id,
      startDate: s.startDate || '',
      endDate: s.endDate   || '',
    })),
    users: selectedUserIds.value.map(toKey),
  }

  

  localStorage.setItem(persistKey.value, JSON.stringify(data))
}
function loadPersist () {
  try {
    const raw = localStorage.getItem(persistKey.value)
    
    return raw ? JSON.parse(raw) : { sites: [], users: [] }
  } catch { return { sites: [], users: [] } }
}
</script>

<template>
  <div>
    <!-- 페이지 헤더 -->
    <h3 class="text-lg font-semibold mb-8">
      사용자
    </h3>

    <VCard>
      <!-- 상단 툴바 -->
      <VCardText>
        <div class="toolbar">
          <div class="left" />
          <div class="right">
            <AppSelect
              v-model="filterItem"
              :items="filterItems"
              style="inline-size:150px"
            />
            <AppTextField
              v-model="search"
              class="pe-4"
              style="inline-size:200px"
              :placeholder="$t('Search') + '...'"
              density="compact"
            />
            <VBtn
              prepend-icon="tabler-plus"
              color="primary"
              :disabled="projectLocked"
              @click="openDialog"
            >
              기관 및 사용자 등록
            </VBtn>
          </div>
        </div>
      </VCardText>

      <!-- 메인 사용자 표 -->
      <VCardText>
        <VDataTable
          v-model="selected"
          v-model:items-per-page="itemsPerPage"
          v-model:page="page"
          :headers="headers"
          :items="users"
          :search="search"
          :custom-filter="filterText"
          density="compact"
          class="custom-table"
          show-select
          item-selectable="selectable"
          @update:options="loadUserTable"
        >
          <template #item.name="{ item }">
            <span :class="{ deleted: item.status !== 'Active' }">{{ item.name }}</span>
          </template>
          <template #item.actions="{ item }">
            <div class="d-flex gap-1">
              <IconBtn
                :disabled="projectLocked"
                @click="$router.push({ name:'sources-users-edit-id', params:{ id:item.id } })"
              >
                <VIcon icon="tabler-edit" />
                <VTooltip
                  activator="parent"
                  location="top"
                >
                  수정
                </VTooltip>
              </IconBtn>
            </div>
          </template>
        </VDataTable>
      </VCardText>
    </VCard>

    <!-- ───────── 모달 ───────── -->
    <VDialog
      v-model="isDialog"
      max-width="1280"
      persistent
    >
      <VCard class="dlg">
        <div class="dlg-head">
          <div class="head-left">
            <span class="dlg-title">기관 및 사용자 등록</span>
          </div>
          <div class="head-right">
            <AppTextField
              v-model="q"
              placeholder="검색(기관/사용자/이메일/역할)"
              density="comfortable"
              hide-details
              style="inline-size: 300px"
              prepend-inner-icon="tabler-search"
            />
          </div>
        </div>

        <VCardText class="dlg-body">
          <!-- 좌: 기관 -->
          <div class="col">
            <div class="col-title">
              기관
            </div>
            <div class="list">
              <div class="list-head sites">
                <div class="c c-chk" />
                <div class="c c-name">
                  기관
                </div>
                <div class="c c-owner">
                  책임자
                </div>
                <div class="c c-date">
                  시작일
                </div>
                <div class="c c-date">
                  종료일
                </div>
              </div>

              <div class="list-body">
                <div
                  v-for="row in filteredSiteRows"
                  :key="row.id"
                  class="list-row sites"
                  :class="{ selected: isSiteSelected(row.id) }"
                  @click="toggleSite(row.id)"
                >
                  <div
                    class="c c-chk"
                    @click.stop
                  >
                    <VCheckbox
                      :model-value="isSiteSelected(row.id)"
                      density="compact"
                      hide-details
                      @update:model-value="() => toggleSite(row.id)"
                    />
                  </div>

                  <div class="c c-name">
                    <div class="primary-text">
                      {{ row.name }}
                      <span
                        v-if="row.registered"
                        class="tag-registered"
                      >이미 등록</span>
                    </div>
                    <div class="sub-text">
                      {{ row.phone }}
                    </div>
                  </div>

                  <div class="c c-owner">
                    <span class="sub-text">{{ row.investigator }}</span>
                  </div>

                  <div
                    class="c c-date"
                    @click.stop
                  >
                    <AppDateTimePicker
                      v-model="row.startDate"
                      append-inner-icon="tabler-calendar-event"
                      class="compact-field"
                      hide-details="auto"
                    />
                  </div>

                  <div
                    class="c c-date"
                    @click.stop
                  >
                    <div class="inline-flex gap-2">
                      <AppDateTimePicker
                        v-model="row.endDate"
                        append-inner-icon="tabler-calendar-event"
                        class="compact-field"
                        hide-details="auto"
                      />
                      <IconBtn
                        size="small"
                        @click="row.endDate = getToday()"
                      >
                        <VIcon icon="tabler-calendar-check" />
                      </IconBtn>
                    </div>
                  </div>
                </div>

                <div
                  v-if="!filteredSiteRows.length"
                  class="empty-hint"
                >
                  등록 가능한 기관이 없습니다.
                </div>
              </div>
            </div>
          </div>

          <!-- 우: 사용자(기관 선택 시에만 표시) -->
          <div
            v-if="selectedSiteIds.length"
            class="col"
          >
            <div class="col-title">
              사용자
            </div>
            <div class="list">
              <div class="list-head users">
                <div class="c c-chk" />
                <div class="c c-name">
                  사용자
                </div>
                <div class="c c-name">
                  기관
                </div> 
              </div>

              <div class="list-body">
                <div
                  v-for="u in filteredUsersForDialog"
                 
                  :key="u.id"
                  class="list-row users"
                  :class="{ disabled: !u.selectable, selected: isUserSelected(u.id) }"
                  @click="toggleUser(u.id, !u.selectable)"
                >
                  <div
                    class="c c-chk"
                    @click.stop
                  >
                    <VCheckbox
                      :model-value="isUserSelected(u.id)"
                      :disabled="!u.selectable"
                      density="compact"
                      hide-details
                      @update:model-value="() => toggleUser(u.id, !u.selectable)"
                    />
                  </div>

                  <div class="c c-name">
                    <div
                      class="primary-text"
                      :class="{ muted: !u.selectable }"
                    >
                      {{ u.name }}
                      <span
                        v-if="u._already"
                        class="tag-registered"
                      >이미 등록</span>
                    </div>
                    <div
                      v-if="!u._hasSite"
                      class="hint-badge"
                    >
                      소속없음
                    </div>
                  </div>
                  <div class="c c-email">
                    <span class="sub-text">{{ u.site_name }}</span>
                  </div> 
                </div>

                <div
                  v-if="selectedSiteIds.length && !filteredUsersForDialog.length"
                  class="empty-hint"
                >
                  선택한 기관에 연결 가능한 사용자가 없습니다.
                </div>
              </div>
            </div>
          </div>

          <!-- 안내(기관 미선택 시) -->
          <div
            v-else
            class="col"
          >
            <div
              class="list"
              style="display:flex;align-items:center;justify-content:center;min-block-size:200px"
            >
              <div class="empty-hint">
                좌측에서 기관을 선택하면 관련 사용자가 표시됩니다.
              </div>
            </div>
          </div>
        </VCardText>

        <div
          class="summary-bar"
          :class="{ empty: !selectedSites.length && !selectedUserIds.length }"
        >
          <VIcon
            v-if="!selectedSites.length && !selectedUserIds.length"
            icon="tabler-info-circle"
            size="18"
            class="me-1"
          />
          {{ summaryText }}
        </div>

        <VCardActions class="dlg-actions">
          <VBtn
            variant="tonal"
            color="secondary"
            @click="closeDialog"
          >
            취소
          </VBtn>
          <VBtn
            color="#3857F5"
            :disabled="!canSave"
            :loading="saving"
            @click="submitAll"
          >
            등록
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
/* 상단/메인 테이블 */
.toolbar{display:flex;justify-content:space-between;gap:12px}
.right{display:flex;align-items:center;gap:8px}
.deleted{text-decoration:line-through}

:deep(.custom-table .v-table__wrapper table){
  th{background:#F3F5F8!important;border-block-end:1px solid #E9EDF2;color:#7E8B97!important;font-size:13px;font-weight:600;padding-block:10px;padding-inline:12px}
  td{border-block-end:1px solid #EDF1F4;color:#344054;font-size:14px;padding-block:8px;padding-inline:12px}
}

/* 모달 */
.dlg{overflow:hidden;border-radius:12px}
.dlg-head{display:flex;align-items:center;justify-content:space-between;border-block-end:1px solid #EEF2F7;padding-block:14px;padding-inline:16px}
.dlg-title{color:#111827;font-size:16px;font-weight:700}
.dlg-body{display:grid;padding:16px;gap:16px;grid-template-columns:1.5fr 1fr}
.col{display:flex;flex-direction:column;min-inline-size:0}
.col-title{color:#111827;font-weight:700;margin-block-end:8px}

/* 리스트 */
.list{overflow:hidden;border:1px solid #E8ECEF;border-radius:10px;background:#fff}
.list-head{position:sticky;z-index:1;display:grid;align-items:center;background:#F7F9FB;border-block-end:1px solid #E9EDF2;color:#7E8B97;font-size:12.5px;font-weight:700;gap:8px;inset-block-start:0;padding-block:10px;padding-inline:12px}
.list-body{overflow:auto;max-block-size:420px}
.list-row{display:grid;align-items:center;border-block-end:1px solid #F1F4F7;gap:8px;padding-block:10px;padding-inline:12px;transition:background .15s}
.list-row:last-child{border-block-end:none}
.list-row:hover{background:#FAFBFD}
.list-row.selected{background:#EEF6FF}
.list-row.disabled{opacity:.55}

/* 열 구성 */
.list-head.sites, .list-row.sites{
  grid-template-columns: 30px 1.4fr 0.5fr .9fr 1.2fr; /* 종료일 열을 살짝 키움 */
}
.list-head.users,.list-row.users{grid-template-columns:36px 1.2fr 1.4fr .8fr}

/* 텍스트/배지 */
.c{min-inline-size:0}

.c.c-date :deep(.v-field){
  justify-content: center;
  min-inline-size: 150px; /* 필요하면 180~200까지 올리세요 */
}
 


.primary-text{color:#111827;font-weight:600;line-height:1.2}
.sub-text{color:#8A94A6;font-size:12px}
.muted{color:#A0A8B5}
.badge-role{display:inline-block;border-radius:999px;background:#EEF2FF;color:#1F3AA3;font-size:12px;font-weight:700;padding-block:2px;padding-inline:8px}
.tag-registered{display:inline-block;border-radius:6px;background:#FFF7E6;color:#8A5A00;font-size:11px;font-weight:700;margin-inline-start:6px;padding-block:2px;padding-inline:6px;vertical-align:top}
.hint-badge{display:inline-block;border-radius:6px;background:#F1F5F9;color:#64748b;font-size:11px;font-weight:600;margin-inline-start:6px;padding-block:2px;padding-inline:6px}

.inline-flex{display:inline-flex;align-items:center}

/* 날짜 입력 컴팩트 */
.compact-field:deep(.v-field){border-radius:8px !important;min-block-size:36px !important}
.compact-field:deep(.v-field__input){padding-block:6px !important}

/* 선택 요약 바 */
.summary-bar{
  display:flex;align-items:center;background:#F8FAFD;border-block-start:1px solid #EEF2F7;
  color:#344054;font-weight:600;gap:6px;padding-block:10px;padding-inline:16px;
}
.summary-bar.empty{color:#7E8B97;font-weight:500}

/* 풋터 */
.dlg-actions{display:flex;justify-content:flex-end;gap:8px;padding-block:12px;padding-inline:16px}

/* 반응형 */
@media (max-width:1024px){.dlg-body{grid-template-columns:1fr}}
</style>
